// Badge field values and print HTML, shared by the single-badge renderer and group "print all".
import { applyTextCase, fitFontSize, textBoxCss } from './badgeText';
import { badgeCategory, resolveBadgeLayout } from './badgeLayout';
import { printBadgeInPage } from './printWindow';

const PHOTO_PLACEHOLDER = 'https://via.placeholder.com/100x100.png?text=Photo';

export function badgeFieldValue(field, userData) {
  if (field.type === 'qrCode') {
    const qrData = userData?.qrCodeBase64 || userData?.qrCode || userData?.qr_code || userData?.qrCodeUrl;
    if (qrData) {
      if (qrData.startsWith('data:') || qrData.startsWith('http')) return qrData;
      return `data:image/png;base64,${qrData}`;
    }
    const fieldValue = userData?.[field.key];
    if (fieldValue && typeof fieldValue === 'string') {
      if (fieldValue.startsWith('data:') || fieldValue.startsWith('http')) return fieldValue;
      if (fieldValue.length > 100) return `data:image/png;base64,${fieldValue}`;
    }
    return 'https://via.placeholder.com/100x100.png?text=QR';
  }
  if (userData && field.key in userData && userData[field.key] !== null && userData[field.key] !== undefined && userData[field.key] !== '') {
    return applyTextCase(userData[field.key], field);
  }
  return field.type === 'image' ? PHOTO_PLACEHOLDER : null;
}

/** Fields that print for this attendee (empty / category-hidden ones dropped, gaps closed). */
export function badgeLayout(badgeConfig, userData) {
  return resolveBadgeLayout(badgeConfig?.fields, {
    valueOf: (field) => badgeFieldValue(field, userData),
    category: badgeCategory(userData),
  });
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Absolutely positioned fields of one badge, in inches. */
export function badgeFieldsHtml(badgeConfig, userData) {
  const layout = badgeLayout(badgeConfig, userData);
  return (badgeConfig.fields || []).map((field) => {
    const placed = layout.get(field);
    if (!placed) return '';
    const value = placed.value;
    const rotation = field.style?.rotation || 0;
    const rotationStyle = rotation !== 0 ? `transform: rotate(${rotation}deg); transform-origin: center center;` : '';
    const box = `position: absolute; left: ${field.x}in; top: ${placed.y}in; width: ${field.width}in; height: ${field.height}in;`;
    if (field.type === 'text') {
      return `<div style="${box} display: flex; align-items: center; justify-content: center;
        font-size: ${fitFontSize(value, field)}pt; color: ${field.style?.color || '#000000'};
        font-weight: ${field.style?.fontWeight || 'normal'}; font-family: ${field.style?.fontFamily || 'Arial, sans-serif'};
        text-align: center; line-height: 1.2; ${textBoxCss(field)} ${rotationStyle}"><span style="max-width: 100%; min-width: 0;">${escapeHtml(value)}</span></div>`;
    }
    if (field.type === 'image') {
      return `<img src="${value}" style="${box} object-fit: cover; ${rotationStyle}" onerror="this.src='${PHOTO_PLACEHOLDER}'" />`;
    }
    if (field.type === 'qrCode' && value) {
      return `<img src="${value}" style="${box} object-fit: contain; ${rotationStyle}" />`;
    }
    return '';
  }).join('');
}

/** A printable document with one page per badge; prints itself on desktop. */
export function badgeDocumentHtml(badgeConfig, pages, { autoPrint = true } = {}) {
  const { width, height } = badgeConfig.paperSize;
  const single = pages.length === 1;
  return `<!DOCTYPE html>
<html>
  <head>
    <title>${single ? 'Print Badge' : `Print ${pages.length} Badges`}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { size: ${width}in ${height}in; margin: 0; }
      html, body { width: ${width}in; ${single ? `height: ${height}in; overflow: hidden;` : ''} margin: 0; padding: 0; }
      .print-badge { width: ${width}in; height: ${height}in; position: relative; background: white; font-family: Arial, sans-serif;
        overflow: hidden; break-after: page; page-break-after: always; }
      .print-badge:last-child { break-after: auto; page-break-after: auto; }
      @media screen {
        html, body { width: auto; height: auto; overflow: auto; }
        body { padding: 20px; background: #f0f0f0; display: flex; flex-direction: column; align-items: center; gap: 16px; min-height: 100vh; }
        .print-badge { box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); }
      }
    </style>
  </head>
  <body>
    ${pages.map((fields) => `<div class="print-badge">${fields}</div>`).join('\n')}
    ${autoPrint ? `<script>
      window.onload = function() { setTimeout(function() { window.print(); }, ${single ? 1000 : 1500}); };
      window.onafterprint = function() { window.close(); };
    </script>` : ''}
  </body>
</html>`;
}

/**
 * Prints several badges in one job. printWindow must be opened synchronously in the click handler
 * (popup blockers); pass null on phones, where the badges print from the current page instead.
 */
export function printBadgeBatch(badgeConfig, users, printWindow) {
  const pages = users.map((user) => badgeFieldsHtml(badgeConfig, user));
  if (!printWindow) {
    return printBadgeInPage({
      fieldsHtml: pages.map((fields) => `<div class="badge-page">${fields}</div>`).join(''),
      widthIn: badgeConfig.paperSize.width,
      heightIn: badgeConfig.paperSize.height,
      pages: pages.length,
    });
  }
  printWindow.document.write(badgeDocumentHtml(badgeConfig, pages));
  printWindow.document.close();
  printWindow.focus();
  return printWindow;
}
